import aiDraftApi from '@/api/aiDraft/index.ts';
import { plainTextToComposerHtml } from '@/components/lead_xpress/aiDraft/draftBodyHtml.ts';
import {
    isEmailAiDraftCancelledResult,
    isEmailAiDraftUnsupportedResult,
} from '@/components/lead_xpress/aiDraft/types.ts';
import type {
    EmailAiDraftAdapter,
    EmailAiDraftFeedbackPayload,
    EmailAiDraftGeneratePayload,
    EmailAiDraftStopPayload,
    EmailAiDraftUpdatePayload,
} from '@/components/lead_xpress/aiDraft/types.ts';

type LeadIdResolver = () => number | null;

const resolveLeadId = (leadId: number | null | undefined, action: 'generate' | 'update') => {
    if (!leadId) {
        throw new Error(`Cannot ${action} an AI draft without a lead id`);
    }

    return leadId;
};

const toComposerReadyDraft = <Draft extends { body: string }>(draft: Draft): Draft => ({
    ...draft,
    body: plainTextToComposerHtml(draft.body),
});

export const createEmailAiDraftApiAdapter = (
    getLeadId: LeadIdResolver = () => null
): EmailAiDraftAdapter => ({
    async generate({
        leadId: payloadLeadId,
        locale,
        requestId,
        existingSubject,
    }: EmailAiDraftGeneratePayload) {
        const leadId = resolveLeadId(payloadLeadId ?? getLeadId(), 'generate');

        const draft = await aiDraftApi.generateEmailDraft(leadId, {
            locale,
            requestId,
            existingSubject: existingSubject ?? null,
        });

        if (isEmailAiDraftCancelledResult(draft)) {
            return draft;
        }

        return toComposerReadyDraft(draft);
    },

    async update({
        leadId: payloadLeadId,
        locale,
        requestId,
        subject,
        body,
        instruction,
    }: EmailAiDraftUpdatePayload) {
        const leadId = resolveLeadId(payloadLeadId ?? getLeadId(), 'update');

        const draft = await aiDraftApi.updateEmailDraft(leadId, {
            locale,
            requestId,
            subject,
            body,
            instructions: instruction,
        });

        if (isEmailAiDraftCancelledResult(draft) || isEmailAiDraftUnsupportedResult(draft)) {
            return draft;
        }

        return toComposerReadyDraft(draft);
    },

    async stop({ leadId, requestId }: EmailAiDraftStopPayload) {
        await aiDraftApi.cancelDraft(leadId, {
            requestId,
            channel: 'email',
        });
    },

    async submitFeedback(payload: EmailAiDraftFeedbackPayload) {
        const leadId = getLeadId();

        if (!leadId) {
            return;
        }

        await aiDraftApi.submitDraftFeedback(leadId, {
            interactionId: payload.interactionId,
            feedback: payload.feedback,
            reasons: payload.reasons,
            details: payload.details,
        });
    },
});

export const emailAiDraftApiAdapter = createEmailAiDraftApiAdapter();
